import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Edit as EditIcon, ArrowBack as BackIcon, People as PeopleIcon } from '@mui/icons-material';
import reservationService from '../services/reservationService';
import GuestInfoStatus from '../components/GuestInfoStatus';

function ReservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReservation();
  }, [id]);

  const loadReservation = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reservationService.getReservation(id);
      setReservation(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'booking confirmed':
        return 'success';
      case 'checked in':
        return 'info';
      case 'checked out':
        return 'default';
      case 'cancelled':
        return 'warning';
      case 'deleted':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/reservations')}>
          Back to Reservations
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Reservation Details</Typography>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/reservations')}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={() => navigate(`/reservations/${id}/guest-info`)}
            color="primary"
          >
            View Guest Info
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/reservations/${id}/edit`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      {/* Guest Information Status */}
      <GuestInfoStatus 
        reservation={reservation} 
        onUpdate={loadReservation}
      />

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Guest Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Guest Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Guest Name
            </Typography>
            <Typography variant="body1">
              {reservation?.guestFirstName} {reservation?.guestLastName}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Phone Number
            </Typography>
            <Typography variant="body1">
              {reservation?.guestPhone || 'N/A'}
            </Typography>
          </Grid>

          {/* Reservation Details */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Reservation Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Confirmation Code
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {reservation?.confirmationCode}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Status
            </Typography>
            <Chip
              label={reservation?.status || 'Unknown'}
              color={getStatusColor(reservation?.status)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Check-in Date
            </Typography>
            <Typography variant="body1">
              {formatDate(reservation?.checkInDate)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Check-out Date
            </Typography>
            <Typography variant="body1">
              {formatDate(reservation?.checkOutDate)}
            </Typography>
          </Grid>

          {/* Property Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Property Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Property Name
            </Typography>
            <Typography variant="body1">
              {reservation?.listingNickname || reservation?.listingTitle || 'N/A'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Address
            </Typography>
            <Typography variant="body1">
              {reservation?.listingAddress || 'N/A'}
            </Typography>
          </Grid>

          {/* Booking Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Booking Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Number of Guests
            </Typography>
            <Typography variant="body1">
              {reservation?.numberOfGuests || 'N/A'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Number of Nights
            </Typography>
            <Typography variant="body1">
              {reservation?.numberOfNights || 'N/A'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Channel
            </Typography>
            <Typography variant="body1">
              {reservation?.channel || 'N/A'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Total Payout
            </Typography>
            <Typography variant="body1">
              {reservation?.totalHostPayout ? `$${reservation.totalHostPayout}` : 'N/A'}
            </Typography>
          </Grid>

          {/* Metadata */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              System Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {formatDate(reservation?.createdAt)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Last Updated
            </Typography>
            <Typography variant="body1">
              {formatDate(reservation?.updatedAt)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default ReservationDetail;
